<div class="row g-4">
  <div class="col-12 col-xl-7">
    <section class="border rounded-4 h-100 p-3 p-lg-4">
      <div class="text-uppercase text-muted fw-semibold small mb-3">Signers, in order</div>
      <p class="text-muted mb-4">
        Pulled from the roles <span class="fw-semibold text-black">{{ selected_document_name }}</span> requires.
        Reorder, add an email, or add another signer.
      </p>

      <div
        v-if="signer_state_message"
        class="border rounded-4 p-3 mb-3"
        :class="load_error ? 'text-danger bg-danger-subtle border-danger-subtle' : 'text-muted bg-light-subtle'"
      >
        {{ signer_state_message }}
      </div>

      <article
        v-for="(slot, index) in display_signer_slots"
        :key="slot.key"
        class="border rounded-4 p-3 mb-3"
      >
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="d-flex align-items-start gap-3">
            <div class="fw-bold text-muted pt-1">{{ index + 1 }}</div>
            <span
              class="esigning-square-225 rounded-circle d-inline-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
              :class="slot.source_type === 'external' ? 'bg-warning' : 'onboarding-bg-navy-dark'"
            >{{ get_slot_initials(slot) }}</span>
            <div>
              <div class="fw-bold text-black">{{ get_slot_display_name(slot) }}</div>
              <div class="text-muted small">
                {{ get_role_label(slot) }}
                <span
                  class="badge rounded-pill ms-2"
                  :class="slot.source_type === 'external' ? 'bg-warning-subtle text-warning-emphasis' : 'bg-secondary-subtle text-secondary-emphasis'"
                >{{ get_slot_badge(slot) }}</span>
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button
              type="button"
              class="btn btn-sm btn-light border rounded-3"
              @click="move_slot(index, -1)"
              :disabled="saving || !can_move_slot(index, -1)"
            >
              <i class="bi bi-chevron-up"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-light border rounded-3"
              @click="move_slot(index, 1)"
              :disabled="saving || !can_move_slot(index, 1)"
            >
              <i class="bi bi-chevron-down"></i>
            </button>
          </div>
        </div>

        <div class="mt-3" v-if="should_show_internal_selector(slot)">
          <label class="form-label fw-semibold text-black mb-2">Select {{ get_role_label_lower(slot) }} party for this role</label>
          <select
            class="form-select rounded-3 mb-2"
            v-model="slot.selected_user_id"
            @change="on_user_selection_change(slot)"
            :disabled="saving"
          >
            <option value="" disabled>Select user</option>
            <option
              v-for="user in get_available_user_options(slot)"
              :key="user.user_id"
              :value="String(user.user_id)"
            >{{ format_user_label(user) }}</option>
            <option :value="'__external__'">Add external signer instead</option>
          </select>
          <input
            type="email"
            class="form-control rounded-3"
            :placeholder="`Email address for ${get_slot_display_name(slot)}`"
            v-model="slot.email"
            :disabled="saving"
          >
        </div>

        <div class="mt-3" v-else>
          <div v-if="get_available_user_options(slot).length" class="d-flex justify-content-end mb-2">
            <button
              type="button"
              class="btn btn-sm btn-light border"
              @click="switch_slot_to_internal(slot)"
              :disabled="saving"
            >
              Use internal user instead
            </button>
          </div>
          <input
            type="text"
            class="form-control rounded-3 mb-2"
            :placeholder="`${get_role_label(slot)} full name`"
            v-model="slot.external_name"
            :disabled="saving"
          >
          <input
            type="email"
            class="form-control rounded-3"
            placeholder="Email address for the signing link"
            v-model="slot.email"
            :disabled="saving"
          >
        </div>

        <div v-if="build_validation_message(slot)" class="small text-warning mt-2">
          {{ build_validation_message(slot) }}
        </div>
      </article>
    </section>
  </div>

  <div class="col-12 col-xl-5">
    <section class="border rounded-4 h-100 p-3 p-lg-4">
      <div class="text-uppercase text-muted fw-semibold small mb-3">How will they sign?</div>

      <article
        v-for="option in signing_options"
        :key="option.id"
        class="border rounded-4 p-3 mb-3"
        :class="signing_method === option.id ? 'border-primary' : ''"
        role="button"
        tabindex="0"
        @click="signing_method = option.id"
      >
        <div class="d-flex align-items-start gap-3">
          <span
            class="esigning-square-175 rounded-circle flex-shrink-0 d-inline-flex align-items-center justify-content-center"
            :class="signing_method === option.id ? 'border border-4 onboarding-border-navy' : 'border'"
          >
            <span
              v-if="signing_method === option.id"
              class="configure-signers-option-indicator-dot rounded-circle onboarding-bg-navy-dark"
            ></span>
          </span>
          <div>
            <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
              <span class="fw-bold text-black">{{ option.label }}</span>
            </div>
            <div class="text-muted">{{ option.description }}</div>
          </div>
        </div>
      </article>

      <div class="d-flex align-items-center gap-3 mt-4 d-none">
        <div class="form-check form-switch m-0">
          <input
            class="configure-signers-switch form-check-input"
            type="checkbox"
            role="switch"
            v-model="notify_in_order"
            :disabled="saving"
          >
        </div>
        <span class="text-black">Notify signers one at a time, in order</span>
      </div>

      <div class="border rounded-4 bg-light-subtle p-3 mt-4 d-none">
        <div class="fw-semibold text-black mb-1">Ready to save</div>
        <div class="text-muted small">
          {{ signer_count_label }} will be saved for {{ notify_in_order ? 'sequential' : 'parallel' }} signing.
        </div>
      </div>
    </section>
  </div>
</div>
